import { useCallback, useEffect, useState } from 'react';

/**
 * A minimal History-API router.
 *
 * The platform has a handful of screens and no nested layouts, so a routing
 * library would be more dependency than the problem deserves — and the
 * RebarFix frontend deliberately keeps its dependency list short too.
 */

export type Route =
  | { name: 'dashboard' }
  | { name: 'tool'; key: string }
  | { name: 'admin' }
  | { name: 'account' }
  // Reached from an email, by someone who is not signed in yet.
  | { name: 'set-password'; token: string; kind: 'invite' | 'reset' }
  | { name: 'forgot-password' }
  | { name: 'sign-up' }
  | { name: 'verify-signup'; token: string }
  | { name: 'not-found'; path: string };

/** Routes that render without a session, because their whole purpose is to
 *  get someone one. */
export function isPublicRoute(route: Route): boolean {
  return (
    route.name === 'set-password' ||
    route.name === 'forgot-password' ||
    route.name === 'sign-up' ||
    route.name === 'verify-signup'
  );
}

export function parsePath(path: string): Route {
  const clean = path.replace(/\/+$/, '') || '/';
  if (clean === '/' || clean === '/tools') return { name: 'dashboard' };
  if (clean === '/admin') return { name: 'admin' };
  if (clean === '/account') return { name: 'account' };
  if (clean === '/forgot-password') return { name: 'forgot-password' };
  if (clean === '/sign-up') return { name: 'sign-up' };

  const verify = /^\/verify\/(.+)$/.exec(clean);
  if (verify) return { name: 'verify-signup', token: decodeURIComponent(verify[1]) };

  const invite = /^\/invite\/(.+)$/.exec(clean);
  if (invite) return { name: 'set-password', token: decodeURIComponent(invite[1]), kind: 'invite' };

  const reset = /^\/reset\/(.+)$/.exec(clean);
  if (reset) return { name: 'set-password', token: decodeURIComponent(reset[1]), kind: 'reset' };

  const tool = /^\/tools\/([A-Za-z0-9_-]+)$/.exec(clean);
  if (tool) return { name: 'tool', key: tool[1] };

  return { name: 'not-found', path: clean };
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case 'dashboard':
      return '/';
    case 'tool':
      return `/tools/${route.key}`;
    case 'admin':
      return '/admin';
    case 'account':
      return '/account';
    case 'forgot-password':
      return '/forgot-password';
    case 'sign-up':
      return '/sign-up';
    case 'verify-signup':
      return `/verify/${encodeURIComponent(route.token)}`;
    case 'set-password':
      return `/${route.kind === 'invite' ? 'invite' : 'reset'}/${encodeURIComponent(route.token)}`;
    case 'not-found':
      return route.path;
  }
}

export function useRouter() {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = pathFor(next);
    if (options?.replace) {
      window.history.replaceState(null, '', path);
    } else {
      window.history.pushState(null, '', path);
    }
    setRoute(next);
    // A route change is a new page as far as the reader is concerned; without
    // this the browser keeps the previous screen's scroll position.
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}
